import { dimensionForUnit } from "./units.js";

const actualFields = new Set([
  "level",
  "quantity",
  "volume",
  "intensity",
  "remaining",
  "soc",
  "current",
  "power",
  "temperature",
  "pressure",
  "production",
  "consumption",
  "autonomy",
  "humidity",
  "wind",
  "depth",
  "speed",
  "heading",
  "bearing",
  "course",
  "set",
  "drift",
  "latitude",
  "longitude",
  "range",
  "cpa",
  "strength",
  "azimuth",
  "elevation",
  "rpm",
  "delta",
  "phase",
]);
const discreteRoles = new Set(["state", "running", "fix", "status", "mode"]);
const circularFields = new Set([
  "heading",
  "bearing",
  "course",
  "set",
  "azimuth",
]);
const forbiddenFields = new Set([
  "now",
  "time",
  "measuredAt",
  "issuedAt",
  "threshold",
  "operationalRadius",
  "watchdogMs",
  "capacity",
  "scaleMin",
  "scaleMax",
]);
const good = (q) => q === undefined || q === "good";
const mod = (v, n) => ((v % n) + n) % n;
const mix = (a, b, t, period = 0, signed = false) => {
  if (!period) return a + (b - a) * t;
  const delta = mod(b - a + period / 2, period) - period / 2;
  const result = a + delta * t;
  return signed
    ? mod(result + period / 2, period) - period / 2
    : mod(result, period);
};

/** Animate presentation only. Histories, quality, permissions and thresholds stay authoritative. */
export function interpolateControlData(from, to, fraction) {
  const t = Math.max(0, Math.min(1, fraction));
  if (!from || !good(from.quality) || !good(to?.quality))
    return structuredClone(to);
  function visit(a, b, key = "", parent = null) {
    if (b === null || typeof b !== "object") {
      if (
        typeof a !== "number" ||
        typeof b !== "number" ||
        !Number.isFinite(a) ||
        !Number.isFinite(b) ||
        forbiddenFields.has(key)
      )
        return b;
      if (key !== "value" && !actualFields.has(key)) return b;
      if (key === "value") {
        if (
          !good(parent?.before?.quality) ||
          !good(parent?.after?.quality) ||
          parent.before.unit !== parent.after.unit ||
          parent.before.reference !== parent.after.reference
        )
          return b;
        if (
          discreteRoles.has(parent.role) ||
          (parent.role !== "time" && (Math.abs(a) > 1e11 || Math.abs(b) > 1e11))
        )
          return b;
        const dimension = dimensionForUnit(parent.after.unit);
        if (dimension === "angle") {
          const period = parent.after.unit === "rad" ? Math.PI * 2 : 360;
          return mix(a, b, t, period, !circularFields.has(parent.role));
        }
      }
      return mix(a, b, t, circularFields.has(key) ? 360 : 0);
    }
    if (
      key === "history" ||
      key === "capabilities" ||
      key === "commandFeedback" ||
      key === "media"
    )
      return b;
    if (Array.isArray(b))
      return b.map((item, index) => {
        const before = item?.id
          ? a?.find?.((candidate) => candidate?.id === item.id)
          : Array.isArray(a) && a.length === b.length
            ? a[index]
            : undefined;
        return visit(before, item, key, parent);
      });
    if (b.quality && (!good(b.quality) || !good(a?.quality)))
      return structuredClone(b);
    return Object.fromEntries(
      Object.entries(b).map(([k, v]) => [
        k,
        visit(a?.[k], v, k, { before: a, after: b, role: key }),
      ]),
    );
  }
  return visit(from, to);
}

export class ControlMotion {
  constructor({
    paint,
    active = () => true,
    request = globalThis.requestAnimationFrame?.bind(globalThis),
    cancel = globalThis.cancelAnimationFrame?.bind(globalThis),
    now = () => performance.now(),
    reduced = () =>
      globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ??
      false,
  }) {
    Object.assign(this, { paint, active, request, cancel, now, reduced });
    this.phase = "settled";
    this.paused = false;
    this.disposed = false;
  }
  update(target, { duration = 1100, enabled = true } = {}) {
    if (this.disposed) return;
    this.cancel?.(this.frame);
    const previous = this.current;
    this.target = target;
    this.duration = Math.max(
      0,
      Math.min(5000, Number.isFinite(duration) ? duration : 0),
    );
    this.enabled = enabled;
    if (
      !previous ||
      !enabled ||
      !this.duration ||
      this.reduced() ||
      !this.active() ||
      !good(previous.quality) ||
      !good(target.quality)
    ) {
      this.finish();
      return;
    }
    this.from = previous;
    this.current = interpolateControlData(previous, target, 0);
    if (!this.paused) this.start();
    this.paint(this.current);
  }
  start() {
    if (!this.request) {
      this.finish();
      return;
    }
    this.phase = "animating";
    this.started = this.now();
    let previous = 0;
    const tick = (now) => {
      if (this.disposed || this.paused) return;
      if (!this.active() || this.reduced()) {
        this.finish();
        return;
      }
      const t = Math.min(1, (now - this.started) / this.duration);
      if (now - previous >= 1000 / 30 || t === 1) {
        this.current =
          t === 1
            ? this.target
            : interpolateControlData(
                this.from,
                this.target,
                t * t * (3 - 2 * t),
              );
        if (t === 1) this.phase = "settled";
        this.paint(this.current);
        previous = now;
      }
      if (t < 1) this.frame = this.request(tick);
    };
    this.frame = this.request(tick);
  }
  finish() {
    this.cancel?.(this.frame);
    this.phase = "settled";
    if (this.target) {
      this.current = this.target;
      this.paint(this.current);
    }
  }
  pause() {
    this.cancel?.(this.frame);
    this.paused = true;
    this.phase = "paused";
  }
  resume() {
    if (this.disposed) return;
    this.cancel?.(this.frame);
    this.paused = false;
    if (!this.target) return;
    if (!this.enabled || !this.duration || this.reduced() || !this.active()) {
      this.finish();
      return;
    }
    this.from = this.current;
    this.start();
  }
  dispose() {
    this.cancel?.(this.frame);
    this.disposed = true;
    this.phase = "disposed";
  }
}

/** Keep SVG nodes stable so media, keyboard focus and command inputs survive every frame. */
export function patchControlSVG(container, markup) {
  const template = document.createElement("template");
  template.innerHTML = markup;
  const next = template.content.querySelector("svg");
  const existing = container.querySelector(":scope > svg");
  if (!existing) {
    container.prepend(next);
    return next;
  }
  function sync(a, b) {
    if (!a || a.nodeType !== b.nodeType || a.nodeName !== b.nodeName) {
      a?.replaceWith(b.cloneNode(true));
      return;
    }
    if (b.nodeType === 3) {
      if (a.nodeValue !== b.nodeValue) a.nodeValue = b.nodeValue;
      return;
    }
    if (b.nodeType !== 1) return;
    for (const attr of [...a.attributes])
      if (!b.hasAttribute(attr.name)) a.removeAttribute(attr.name);
    for (const attr of b.attributes)
      if (a.getAttribute(attr.name) !== attr.value)
        a.setAttribute(attr.name, attr.value);
    const oldChildren = [...a.childNodes],
      newChildren = [...b.childNodes];
    newChildren.forEach((child, index) =>
      oldChildren[index]
        ? sync(oldChildren[index], child)
        : a.append(child.cloneNode(true)),
    );
    oldChildren.slice(newChildren.length).forEach((child) => child.remove());
  }
  sync(existing, next);
  return existing;
}
